import type { Ref } from "vue";

const AXIS_LOCK_PX = 10;
const CLOSE_DISTANCE_PX = 72;
const CLOSE_FLICK_DISTANCE_PX = 40;
const CLOSE_VELOCITY_PX_MS = 0.55;

type SwipeAxis = "horizontal" | "vertical" | null;

export function useSwipeToClose(options: {
	el: Ref<HTMLElement | null | undefined>;
	enabled: () => boolean;
	onOffset: (offset: number | null) => void;
	onClose: () => void;
}) {
	let pointerId: number | null = null;
	let startX = 0;
	let startY = 0;
	let lastX = 0;
	let lastT = 0;
	let velocityX = 0;
	let axis: SwipeAxis = null;
	let dragging = false;
	let suppressClick = false;

	function reset(emitOffset = true) {
		pointerId = null;
		axis = null;
		dragging = false;
		velocityX = 0;
		if (emitOffset) {
			options.onOffset(null);
		}
	}

	function onPointerDown(event: PointerEvent) {
		if (!options.enabled()) return;
		if (event.pointerType === "mouse" && event.button !== 0) return;

		pointerId = event.pointerId;
		startX = lastX = event.clientX;
		startY = event.clientY;
		lastT = performance.now();
		velocityX = 0;
		axis = null;
		dragging = false;
	}

	function onPointerMove(event: PointerEvent) {
		if (pointerId !== event.pointerId) return;

		const dx = event.clientX - startX;
		const dy = event.clientY - startY;
		const now = performance.now();

		if (!axis) {
			if (Math.hypot(dx, dy) < AXIS_LOCK_PX) return;
			axis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
			if (axis === "vertical") {
				reset(false);
				return;
			}
			dragging = true;
			options.el.value?.setPointerCapture(event.pointerId);
		}

		if (axis !== "horizontal") return;

		const dt = Math.max(1, now - lastT);
		velocityX = (event.clientX - lastX) / dt;
		lastX = event.clientX;
		lastT = now;

		const maxOffset = options.el.value?.offsetWidth ?? 360;
		options.onOffset(Math.min(maxOffset, Math.max(0, -dx)));
	}

	function onPointerUp(event: PointerEvent) {
		if (pointerId !== event.pointerId) return;

		const offset = Math.max(0, -(event.clientX - startX));
		const shouldClose = dragging && (
			offset >= CLOSE_DISTANCE_PX ||
			(offset >= CLOSE_FLICK_DISTANCE_PX && velocityX <= -CLOSE_VELOCITY_PX_MS)
		);

		if (dragging && offset > AXIS_LOCK_PX) {
			suppressClick = true;
		}

		pointerId = null;
		axis = null;
		dragging = false;
		velocityX = 0;

		if (shouldClose) {
			// Keep the current drag offset for one frame so the parent can
			// transition from the dragged position to the closed position.
			options.onClose();
		} else {
			options.onOffset(null);
		}
	}

	function onPointerCancel(event: PointerEvent) {
		if (pointerId !== event.pointerId) return;
		reset();
	}

	function onClickCapture(event: MouseEvent) {
		if (!suppressClick) return;
		suppressClick = false;
		event.preventDefault();
		event.stopPropagation();
	}

	return {
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerCancel,
		onClickCapture,
		reset,
	};
}
